/**
 * useNotificationSSE Composable
 *
 * Manages a Server-Sent Events (SSE) connection for real-time notification delivery.
 * Opens once when called (typically from a root layout) and persists across route changes.
 * Implements auto-reconnect with exponential back-off on network failure.
 *
 * SSE Event Types (from backend):
 *  - NOTIFICATION  — New notification arrives
 *  - READ_RECEIPT  — Notification marked as read (multi-tab sync)
 *  - ACTIONED      — CRITICAL notification acknowledged (multi-tab sync)
 *  - Heartbeat (`: heartbeat\n\n`) every 30s — keeps connection alive
 *
 * Features:
 *  - Typed event handling for multi-tab sync
 *  - Auto-reconnect with exponential backoff (2s → 4s → 8s → 16s → 30s max)
 *  - Exposes `reconnect()` for callers to invoke after token refresh
 *  - Clean shutdown on unmount / logout
 *
 * @example
 * ```ts
 * const { isConnected } = useNotificationSSE({
 *   url: '/api/v1/notifications/stream',
 *   getToken: () => authStore.accessToken, // frontend-only auth guard
 *   getUserId: () => authStore.staffId,
 *   onNotification: (notification) => store.handleIncoming(notification),
 *   onReadReceipt: (id) => store.handleReadReceipt(id),
 *   onActioned: (id) => store.handleActioned(id),
 *   onConnectionChange: (connected) => store.setConnectionState(connected),
 *   enabled: computed(() => authStore.isAuthenticated),
 * });
 * ```
 */
import { ref, watch, onUnmounted, type Ref, type ComputedRef } from 'vue';

export interface NotificationSSEOptions {
  /**
   * SSE endpoint URL (relative or absolute).
   * `user_id` will be appended as a query param. Auth is handled by the API
   * gateway (cookie / header), not by a token in the URL.
   */
  url: string;

  /**
   * Function that returns the current access token (JWT).
   * Used as a **frontend-only guard**: the composable will not open an SSE
   * connection unless a token is available (i.e. the user is authenticated).
   * The token is NOT sent to the SSE endpoint — auth flows through the API
   * gateway.
   */
  getToken: () => string | null;

  /**
   * Function that returns the current authenticated user's UUID.
   * Required by the backend SSE endpoint (`?user_id=<uuid>`).
   */
  getUserId: () => string | null;

  /**
   * Callback fired when a new NOTIFICATION event arrives via SSE.
   */
  onNotification: (notification: SSENotificationPayload) => void;

  /**
   * Callback fired when a READ_RECEIPT event arrives (multi-tab sync).
   * The recipient_id that was marked as read in another tab/window,
   * or null when markAllRead was called.
   */
  onReadReceipt?: (recipientId: string | null) => void;

  /**
   * Callback fired when an ACTIONED event arrives (multi-tab sync).
   * The recipient_id of a CRITICAL alert acknowledged in another tab.
   */
  onActioned?: (recipientId: string) => void;

  /**
   * Callback fired when a FORCE_LOGOUT event arrives — pushed by the backend
   * right after an admin disables this user's account. No DB write, not tied
   * to a recipient_id; the caller should clear auth state and redirect.
   */
  onForceLogout?: (payload: SSEForceLogoutPayload) => void;

  /**
   * Optional callback fired when connection state changes.
   */
  onConnectionChange?: (connected: boolean) => void;

  /**
   * Reactive flag controlling whether SSE should be active.
   * When false, connection is closed. When true, it (re)connects.
   * Defaults to always-enabled if not provided.
   */
  enabled?: Ref<boolean> | ComputedRef<boolean>;

  /**
   * Maximum number of reconnect attempts before giving up.
   * Set to Infinity for unlimited retries (default).
   */
  maxRetries?: number;
}

/**
 * Shape of a notification payload received via SSE NOTIFICATION event.
 *
 * Field names match the backend InAppChannel output (snake_case).
 * `recipient_id` is the notification_recipients PK used for read/actioned ops.
 */
export interface SSENotificationPayload {
  /** notification_recipients.recipient_id — primary identifier for mark-read / actioned */
  recipient_id: string;
  /** notification_messages.message_id */
  message_id: string;
  subject: string;
  rendered_body: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
  event_type: string;
  reference_entity_type: string | null;
  reference_entity_id: string | null;
  created_at: string;
}

/**
 * Shape of a READ_RECEIPT SSE event payload.
 * `recipient_id` is null when markAllRead was called.
 */
export interface SSEReadReceiptPayload {
  recipient_id: string | null;
}

/**
 * Shape of an ACTIONED SSE event payload.
 */
export interface SSEActionedPayload {
  recipient_id: string;
}

/**
 * Shape of a FORCE_LOGOUT SSE event payload (see @tms-phoenix/tms-types ForceLogoutEvent).
 */
export interface SSEForceLogoutPayload {
  reason: 'ACCOUNT_DISABLED' | 'ACCOUNT_DELETED' | 'SESSION_REVOKED';
  timestamp: string;
}

/** Reconnect delays in ms: 2s → 4s → 8s → 16s → 30s (capped) */
const RECONNECT_DELAYS = [2000, 4000, 8000, 16000, 30000];

export function useNotificationSSE(options: NotificationSSEOptions) {
  const {
    url,
    getToken,
    getUserId,
    onNotification,
    onReadReceipt,
    onActioned,
    onForceLogout,
    onConnectionChange,
    enabled,
    maxRetries = Infinity,
  } = options;

  const isConnected = ref(false);
  const connectionError = ref<string | null>(null);
  const retryCount = ref(0);

  let eventSource: EventSource | null = null;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;
  let isDestroyed = false;

  /**
   * Open the SSE connection with the current user ID.
   * Requires a valid access token (frontend guard) but does not send it;
   * the backend SSE endpoint only uses `?user_id=<uuid>` and relies on
   * gateway-level auth.
   */
  function connect() {
    // Don't connect if destroyed or disabled
    if (isDestroyed) return;
    if (enabled?.value === false) return;

    const token = getToken();
    if (!token) {
      connectionError.value = 'No access token available';
      return;
    }

    const userId = getUserId();
    if (!userId) {
      connectionError.value = 'No user ID available';
      return;
    }

    // Close any existing connection
    disconnect();

    try {
      // The backend SSE endpoint only requires `user_id` as a query param.
      // Auth is handled at the API gateway layer (cookies / Authorization header
      // via withCredentials). We do NOT append the token to the URL to avoid
      // leaking JWTs in server logs, browser history, and referrer headers.
      const separator = url.includes('?') ? '&' : '?';
      const sseUrl = `${url}${separator}user_id=${encodeURIComponent(userId)}`;

      eventSource = new EventSource(sseUrl, { withCredentials: true });

      eventSource.onopen = () => {
        isConnected.value = true;
        connectionError.value = null;
        retryCount.value = 0; // Reset backoff on successful connection
        onConnectionChange?.(true);
      };

      // ── Unnamed SSE Event Handler ───────────────────────────────────────────
      // Backend sends unnamed data events with a `type` field in the JSON payload:
      //   data: {"type":"NOTIFICATION","recipient_id":"...","subject":"...",...}
      //   data: {"type":"READ_RECEIPT","recipient_id":"..."}
      //   data: {"type":"ACTIONED","recipient_id":"..."}
      // We route messages by parsing `type` from the payload.
      eventSource.onmessage = (event: MessageEvent) => {
        retryCount.value = 0;
        try {
          const payload = JSON.parse(event.data);
          const eventType = payload?.type;

          switch (eventType) {
            case 'NOTIFICATION':
              onNotification(payload as SSENotificationPayload);
              break;
            case 'READ_RECEIPT':
              onReadReceipt?.((payload as SSEReadReceiptPayload).recipient_id);
              break;
            case 'ACTIONED':
              onActioned?.((payload as SSEActionedPayload).recipient_id);
              break;
            case 'FORCE_LOGOUT':
              onForceLogout?.(payload as SSEForceLogoutPayload);
              break;
            case undefined:
              // Legacy payload without a type field — route to onNotification
              // for backward compatibility with backends that omit `type`.
              // Validate payload has required fields to avoid forwarding null/empty objects.
              if (payload && typeof payload === 'object' && payload.recipient_id) {
                onNotification(payload as SSENotificationPayload);
              } else {
                console.warn('[NotificationSSE] Received payload without type or recipient_id — ignoring:', payload);
              }
              break;
            default:
              // Truly unrecognized type — log and skip
              console.warn('[NotificationSSE] Unrecognized SSE event type:', eventType);
              break;
          }
        } catch (err) {
          console.warn('[NotificationSSE] Failed to parse SSE message:', err);
        }
      };

      eventSource.onerror = () => {
        // EventSource auto-closes on error — we manage reconnect manually
        isConnected.value = false;
        onConnectionChange?.(false);
        eventSource?.close();
        eventSource = null;

        // Schedule reconnect with exponential backoff
        if (retryCount.value < maxRetries && !isDestroyed) {
          const delay =
            RECONNECT_DELAYS[Math.min(retryCount.value, RECONNECT_DELAYS.length - 1)];
          retryCount.value++;
          connectionError.value = `Reconnecting in ${delay / 1000}s (attempt ${retryCount.value})`;

          retryTimeout = setTimeout(() => {
            if (!isDestroyed) connect();
          }, delay);
        } else {
          connectionError.value = 'Max reconnection attempts reached';
        }
      };
    } catch (err) {
      connectionError.value = err instanceof Error ? err.message : 'Failed to connect';
    }
  }

  /**
   * Close the SSE connection and cancel any pending retry.
   */
  function disconnect() {
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    isConnected.value = false;
    onConnectionChange?.(false);
  }

  /**
   * Force reconnect (e.g. after token refresh).
   */
  function reconnect() {
    retryCount.value = 0;
    connect();
  }

  // Watch the enabled flag — connect/disconnect reactively
  if (enabled) {
    watch(
      enabled,
      (isEnabled) => {
        if (isEnabled) {
          connect();
        } else {
          disconnect();
          retryCount.value = 0;
        }
      },
      { immediate: true },
    );
  } else {
    // If no enabled flag provided, connect immediately
    connect();
  }

  // Clean up on component unmount
  onUnmounted(() => {
    isDestroyed = true;
    disconnect();
  });

  return {
    /** Whether the SSE connection is currently open */
    isConnected,
    /** Current connection error message (null when healthy) */
    connectionError,
    /** Current retry attempt count */
    retryCount,
    /** Manually reconnect (resets backoff) */
    reconnect,
    /** Manually disconnect */
    disconnect,
  };
}
